import { createContext, useContext, useId, useState, type ReactNode } from 'react';
import { useShellTexts } from '../i18n/use-shell-texts';

/**
 * The legacy `.card`: white, 1px border, 4px radius, no shadow. `CardHeader` and `CardFooter`
 * sit on the light cap background; header actions are small buttons on the right.
 */
export function Card({ className = '', children }: { className?: string; children: ReactNode }) {
  // Inside `EmbeddedCards` the first card down is drawn flat: its frame is the host card's.
  if (useContext(EmbedNextCard)) {
    return (
      <EmbedNextCard.Provider value={false}>
        <FlatCard.Provider value={true}>
          <div className={`min-w-0 ${className}`}>{children}</div>
        </FlatCard.Provider>
      </EmbedNextCard.Provider>
    );
  }
  return (
    <FlatCard.Provider value={false}>
      <div className={`mb-6 min-w-0 rounded-bs border border-line bg-white ${className}`}>
        {children}
      </div>
    </FlatCard.Provider>
  );
}

/** The next `Card` down should be drawn flat (see `EmbeddedCards`). */
const EmbedNextCard = createContext(false);
/** This header belongs to a card drawn flat inside another. */
const FlatCard = createContext(false);

/**
 * Render a whole page that draws its own `Card` (a list page's "Biloversigt", "Kunder", …) as
 * the body of another card — e.g. a `CollapsibleCard` on an overview — without a card inside a
 * card. The first `Card` below this loses its border, radius and margin, and its header turns
 * into a white toolbar under the host card's header - without its title, which the host's header
 * already says. Cards nested further down are untouched.
 */
export function EmbeddedCards({ children }: { children: ReactNode }) {
  return <EmbedNextCard.Provider value={true}>{children}</EmbedNextCard.Provider>;
}

export function CardHeader({
  title,
  actions,
  className = '',
  children,
}: {
  /** Shown bold at body size. */
  title?: ReactNode;
  /** Right-aligned, typically `Button size="sm"`. */
  actions?: ReactNode;
  className?: string;
  /** Free content after the title (e.g. tabs). */
  children?: ReactNode;
}) {
  const flat = useContext(FlatCard);
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 border-b border-line px-5 py-3 ${flat ? 'bg-white' : 'rounded-t-bs bg-cap'} ${className}`}
    >
      {/* A flat card's title is the host card's header, right above it. */}
      {title && !flat ? <strong className="text-sm">{title}</strong> : null}
      {children}
      {actions ? <div className="ml-auto flex flex-wrap items-center gap-2">{actions}</div> : null}
    </div>
  );
}

export function CardBody({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return <div className={`p-5 ${className}`}>{children}</div>;
}

export function CardFooter({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-b-bs border-t border-line bg-cap px-5 py-3 ${className}`}>
      {children}
    </div>
  );
}

/**
 * A card whose body folds away behind a VIS/LUK switch in the header (legacy
 * `switch switch-sm switch-text switch-primary`).
 *
 * `lazy`: the body is not mounted until the card is first opened (and stays mounted after, so
 * closing it keeps its state). For a body that fetches - an overview of several list cards
 * should not run every list's queries for cards nobody opened.
 */
export function CollapsibleCard({
  title,
  actions,
  defaultOpen = true,
  lazy = false,
  className = '',
  children,
}: {
  title: ReactNode;
  actions?: ReactNode;
  defaultOpen?: boolean;
  lazy?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const t = useShellTexts().kit;
  const [open, setOpen] = useState(defaultOpen);
  // Whether the body has ever been shown - a lazy body mounts then and stays mounted.
  const [opened, setOpened] = useState(defaultOpen);
  const bodyId = useId();
  return (
    <Card className={className}>
      <CardHeader
        title={title}
        className={open ? '' : 'rounded-b-bs border-b-0'}
        actions={
          <>
            {actions}
            <button
              type="button"
              role="switch"
              aria-checked={open}
              aria-controls={bodyId}
              aria-label={typeof title === 'string' ? title : undefined}
              onClick={() => {
                setOpen(!open);
                setOpened(true);
              }}
              className={`relative inline-flex h-5 w-12 items-center rounded-full border text-[10px] font-semibold transition-colors ${open ? 'border-brand-border bg-brand text-white' : 'border-line bg-white text-ink-muted'}`}
            >
              <span className={`absolute ${open ? 'left-1.5' : 'right-1.5'}`} aria-hidden="true">
                {open ? t.hide : t.show}
              </span>
              <span
                aria-hidden="true"
                className={`absolute top-0.5 h-3.5 w-3.5 rounded-full border border-line bg-white transition-all ${open ? 'right-0.5' : 'left-0.5'}`}
              />
            </button>
          </>
        }
      />
      <div id={bodyId} hidden={!open}>
        {!lazy || opened ? children : null}
      </div>
    </Card>
  );
}
